// external
import { useFieldArray, useForm } from "react-hook-form";
import classNames from "classnames";

// components
import CustomButton from "components/partials/custom-button/custom-button";
import CustomTextInput from "components/partials/custom-text-input/custom-text-input";

// assets
import Icons from "assets/icons";

function DimensionEdit({
  dimension,
  setDimension,
  heading,
  setPopupOpen,
  type,
}) {
  const dimensionCurrent = dimension.map((item, ind) => ({
    from: item[0],
    to: item[1],
  }));

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: { dimensions: dimensionCurrent },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "dimensions",
  });

  const handleSaveDimensions = (data) => {
    if (type === "rank") {
      setDimension(
        data.dimensions.map((item, index) => {
          const itemTo =
            index < data.dimensions.length - 1
              ? data.dimensions[index + 1].from - 1
              : 999;
          return [item.from, itemTo];
        })
      );
    } else {
      setDimension(data.dimensions.map((item, index) => [item.from, item.to]));
    }
    setPopupOpen(false);
  };

  const renderDimensionFields = () => {
    return fields.map((val, ind) => {
      return (
        // for the from (range start): minimum and maximum is the range end of the previous row
        <div key={ind} className="flex gap-5">
          <CustomTextInput
            {...register(`dimensions.${ind}.from`, {
              required:
                type === "range"
                  ? "From value is required"
                  : "Rank is required",
              valueAsNumber: true,
              max: {
                value:
                  ind > 0
                    ? watch(
                        `dimensions.${ind - 1}.${
                          type === "range" ? "to" : "from"
                        }`
                      ) + 1
                    : 0,
                message:
                  ind > 0
                    ? "Dimensions can't have gaps"
                    : "Dimension can't be negative",
              },
              min: {
                value:
                  ind > 0
                    ? watch(
                        `dimensions.${ind - 1}.${
                          type === "range" ? "to" : "from"
                        }`
                      ) + 1
                    : 0,
                message:
                  ind > 0
                    ? "Dimensions can't overlap"
                    : "Dimension can't be negative",
              },
            })}
            id={`dimensions.${ind}.from`}
            required={true}
            label={type === "range" ? "From" : "Rank"}
            labelInTop={true}
            inputCustomClass={classNames("px-3 py-2", {
              "w-[400px]": type === "rank",
              "w-[200px]": type === "range",
            })}
            customClass="text-lg text-gray-05"
            errorsNames={
              errors.dimensions?.[ind]?.from
                ? { message: errors.dimensions?.[ind]?.from?.message }
                : {}
            }
            type="number"
            isErrorMessageHidden={false}
          />
          {type === "range" && (
            <CustomTextInput
              {...register(`dimensions.${ind}.to`, {
                required: "To value is required",
                min: {
                  value: watch(`dimensions.${ind}.from`),
                  message: "Must exceed start value",
                },
                valueAsNumber: true,
              })}
              id={`dimensions.${ind}.to`}
              required={true}
              label="To"
              labelInTop={true}
              inputCustomClass="px-3 py-2 w-[200px]"
              customClass="text-lg text-gray-05"
              type="number"
              errorsNames={
                errors.dimensions?.[ind]?.to
                  ? { message: errors.dimensions?.[ind]?.to?.message }
                  : {}
              }
            />
          )}
          <CustomButton
            size="sm"
            iconBefore={<Icons.Delete aria-label="Delete Dimension" />}
            onClick={() => remove(ind)}
            customClass="text-blue-01 size-2 hover:text-blue-03 transition-all mt-12"
            disabled={fields.length === 1}
          />
        </div>
      );
    });
  };

  const handlePopupClose = () => {
    setPopupOpen(false);
  };
  return (
    <div className="fixed bottom-0 left-0 right-0 top-0 z-10">
      <div
        className="fixed bottom-0 left-0 right-0 top-0 bg-gray-07/60 transition-opacity"
        aria-hidden="true"
      ></div>
      <div className="fixed bottom-0 left-0 right-0 top-0 flex items-center justify-center">
        <div className="rounded-md bg-white p-6 pl-10">
          <div className="mb-4 flex items-center justify-between">
            <h1 className="font-family text-2xl font-semibold text-blue-03">
              {heading}
            </h1>
            <CustomButton
              iconBefore={
                <Icons.Plus customClass="h-4 w-4 rotate-45 text-blue-03" />
              }
              onClick={handlePopupClose}
            ></CustomButton>
          </div>
          <div className="max-h-[80vh] overflow-y-auto overflow-x-hidden py-2">
            <form onSubmit={handleSubmit(handleSaveDimensions)}>
              {renderDimensionFields()}
              <CustomButton
                variant="secondary"
                iconBefore={
                  <Icons.Plus
                    aria-label="Add Dimension"
                    customClass="h-4 w-4"
                  />
                }
                customClass="mt-4"
                onClick={() => append({ from: "", to: "" })}
              >
                Add Dimension
              </CustomButton>
              <div className="my-2 flex justify-end">
                <CustomButton
                  onClick={() => setPopupOpen(false)}
                  variant="text"
                >
                  Cancel
                </CustomButton>
                <CustomButton variant="primary" type="submit">
                  Save Changes
                </CustomButton>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DimensionEdit;
